/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { useEffect, useState } from "react";

import { useDispatch } from "react-redux";
import styled from "styled-components";
import { useInterval } from "usehooks-ts";

import { ONE_SECOND } from "@vienna/common";

import { useCurrentUser } from "src/app/useCurrentUser";
import { Plate } from "src/classes/plate/Plate";
import { SyncStatusAlertInfo } from "src/components/Plate/Status/SyncStatusAlertInfo";
import { Alert } from "src/components/UI/Alert/Alert";
import { Tooltip } from "src/components/UI/Tooltip";
import { clientLogger } from "src/lib/logging/datadog";
import { safeSegmentTrack } from "src/lib/logging/segment";
import { globalUIActions } from "src/store/modules/globalUI";

export function SyncStatusIcon({ plate }: { plate: Plate }) {
  const dispatch = useDispatch();
  const isConnected = plate.socket.observe((state) => state.connected);
  const awaitingReceipts = plate.socket.observe((state) => state.awaitingReceipt);
  const awaitingSend = plate.socket.observe((state) => state.awaitingSend);

  const currentUser = useCurrentUser()!;
  // state
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [activeNoSave, setActiveNoSave] = useState(false); // there are unsaved transactions that should've gotten a receipt
  const [saveErrorAlertOpen, setSaveErrorAlertOpen] = useState(false);

  // calculated
  const sendsWithoutReceipts = awaitingReceipts.length;
  const totalUnsaved = sendsWithoutReceipts + (awaitingSend || 0);
  const beenWhile =
    awaitingReceipts.length &&
    currentTime -
      Math.min(
        ...awaitingReceipts.map(({ time, size }) => Math.min(+time, +time - size * 0.0005)) // give big updates a little more time
      ) >
      ONE_SECOND * 10;

  useInterval(() => {
    setCurrentTime(Date.now());
  }, ONE_SECOND / 2);

  useEffect(() => {
    if (!isConnected && awaitingReceipts.length) {
      clientLogger.warn(`User ${currentUser.snapshot.username} needs to reconnect to socket`, {
        userId: currentUser.snapshot.id,
        area: "socket",
      });
    } else if (saveErrorAlertOpen && !beenWhile) {
      setActiveNoSave(false);
      clientLogger.info(`No save mode recovered for user ${currentUser.snapshot.username}`, { area: "socket" });
    }
  }, [isConnected, awaitingReceipts.length]);

  if (beenWhile && !saveErrorAlertOpen) {
    clientLogger.error(`User ${currentUser.snapshot.username} entered no save mode`, {
      userId: currentUser.snapshot.id,
      isConnected,
      area: "socket",
    });
    safeSegmentTrack({ event: "No Save Mode Entered" });
    dispatch(globalUIActions.enteredNoSaveMode()); // to track in logrocket
    setActiveNoSave(true);
    setSaveErrorAlertOpen(true);
  }

  const handleSaveErrorAccept = () => {
    setSaveErrorAlertOpen(false);
  };

  const closeSaveErrorAlert = () => {
    setSaveErrorAlertOpen(false);
    window.location.reload();
  };

  // constants for the state of no save mode
  const noSaveRecovered = !activeNoSave && !beenWhile && saveErrorAlertOpen; // true when no save mode was entered (alert is open) and is now recovered
  const noSaveConnectionLost = !noSaveRecovered && !isConnected && saveErrorAlertOpen; // true when no save mode was entered (alert is open) because the connection was lost
  // text for the no save error alert
  const headerText = noSaveRecovered
    ? "Connection to the server was reestablished."
    : noSaveConnectionLost
    ? "Connection lost."
    : "Changes are out of sync with the server.";
  const bodyText = noSaveRecovered
    ? "Changes were saved, you can go back to editing."
    : noSaveConnectionLost
    ? "You can reload or wait for the connection to be reestablished. Warning: reloading may result in data loss."
    : "You can reload or wait for the changes to sync. Warning: reloading may result in data loss.";

  const SpecificStatus = (
    <>
      <div>Websocket: {isConnected?.toString() || "false"}</div>
      <div>About to send: {awaitingSend}</div>
      <div>Changes in transit: {sendsWithoutReceipts}</div>
    </>
  );

  return (
    <Tooltip tooltipText={SpecificStatus}>
      <SyncStatusIconStyled
        style={{ backgroundColor: beenWhile ? "#db4112" : totalUnsaved === 0 ? "#4D9090" : "#F2E294" }}
      />
      {/* out of sync with server */}
      <Alert
        type={noSaveRecovered ? "notification" : "question"}
        // on accept and on decline used in "question" type (when unrecovered)
        acceptText="Wait"
        onAccept={handleSaveErrorAccept}
        declineText="Reload"
        onDecline={closeSaveErrorAlert}
        declineDangerous
        // on okay used in "notification" type (if/when recovered)
        okayText="Okay"
        onOkay={closeSaveErrorAlert}
        isOpen={saveErrorAlertOpen}
        headerText={headerText}
        bodyText={bodyText}
      >
        {!noSaveRecovered && <SyncStatusAlertInfo plate={plate} />}
      </Alert>
    </Tooltip>
  );
}

const SyncStatusIconStyled = styled.div`
  width: 12px;
  height: 12px;
  /* Accent/Smalt Blue */
  background-color: #4d9090;
  margin-right: 14px;
  transition: background-color 75ms ease-in;
`;
